import React, { useState, useMemo } from 'react';
import { 
  UserPlus, 
  Activity,
  Crown, 
  Gem, 
  Coins, 
  Flame, 
  BarChart3, 
  AlertTriangle, 
  CheckCircle2, 
  RefreshCw, 
  Bot,
  ArrowUpRight,
  Calendar
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { useAdminDashboardStats } from '../../hooks/useQueries';
import { DashboardTopApp } from '../../types';
import { DateRangePicker, DateRangeValue, formatDM, formatYMD } from './DateRangePicker';

// Danh mục định danh ứng dụng cho hiển thị thân thiện
const APP_NAMES: Record<string, { name: string; icon: string; tag: string }> = {
  giaoan: { name: 'Soạn Giáo án AI', icon: '📝', tag: 'Giáo án' },
  slide: { name: 'Thiết kế Bài giảng Slide', icon: '📊', tag: 'Thuyết trình' },
  dethi: { name: 'Ngân hàng Đề thi & Trắc nghiệm', icon: '📑', tag: 'Đề thi' },
  troly: { name: 'Trợ lý Giáo viên AI', icon: '🤖', tag: 'Trợ lý' },
  chamdiem: { name: 'Chấm điểm Tự luận & Trắc nghiệm', icon: '✍️', tag: 'Đánh giá' },
  hoso: { name: 'Hồ sơ Giáo viên Điện tử', icon: '📂', tag: 'Hồ sơ' }
};

export const AdminDashboard: React.FC = () => {
  // Helper to initialize default range (7 days)
  const getInitialRange = (): DateRangeValue => {
    const today = new Date();
    const last7 = new Date(today);
    last7.setDate(today.getDate() - 6);
    return {
      startDate: formatYMD(last7),
      endDate: formatYMD(today),
      presetKey: '7_days',
      label: '7 ngày qua'
    };
  };

  const [dateRange, setDateRange] = useState<DateRangeValue>(getInitialRange);

  // Filter param passed to useAdminDashboardStats
  // Presets (7, 30, 90) use ?days=x
  // Other ranges and custom date pickers use ?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
  const currentFilter = useMemo(() => {
    if (dateRange.presetKey === '7_days') return 7;
    if (dateRange.presetKey === '30_days') return 30;
    if (dateRange.presetKey === '90_days') return 90;
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    };
  }, [dateRange]);

  const { 
    data: dashboardData, 
    isLoading, 
    isError, 
    error, 
    refetch, 
    isFetching 
  } = useAdminDashboardStats(currentFilter);

  const summary = dashboardData?.summary;
  const chartData = dashboardData?.chartData || [];
  const topApps = dashboardData?.topApps || [];

  // Helper formatters
  const formatNumber = (val?: number) => {
    if (val === undefined || val === null || isNaN(val)) return '0';
    return Number(val).toLocaleString('vi-VN');
  };

  // Helper text label for timeframe on summary cards
  const getTimeframeLabel = (capitalize = false) => {
    if (dateRange.presetKey === '7_days') return `${capitalize ? 'Trong' : 'trong'} 7 ngày qua`;
    if (dateRange.presetKey === '30_days') return `${capitalize ? 'Trong' : 'trong'} 30 ngày qua`;
    if (dateRange.presetKey === '90_days') return `${capitalize ? 'Trong' : 'trong'} 90 ngày qua`;
    if (dateRange.presetKey === 'today') return `${capitalize ? 'Trong' : 'trong'} hôm nay`;
    if (dateRange.presetKey === 'yesterday') return `${capitalize ? 'Trong' : 'trong'} hôm qua`;
    if (dateRange.presetKey === '14_days') return `${capitalize ? 'Trong' : 'trong'} 14 ngày qua`;
    if (dateRange.presetKey === 'this_month') return `${capitalize ? 'Trong' : 'trong'} tháng này`;
    if (dateRange.presetKey === 'last_month') return `${capitalize ? 'Trong' : 'trong'} tháng trước`;
    if (dateRange.presetKey === 'this_year') return `${capitalize ? 'Trong' : 'trong'} năm nay`;
    if (dateRange.presetKey === 'last_year') return `${capitalize ? 'Trong' : 'trong'} năm trước`;
    if (dateRange.presetKey === 'all_time') return `${capitalize ? 'Toàn' : 'toàn'} bộ thời gian`;

    return `${capitalize ? 'Từ' : 'từ'} ${formatDM(dateRange.startDate)} đến ${formatDM(dateRange.endDate)}`;
  };

  return (
    <div className="space-y-6">
      {/* ========================================================================= */}
      {/* PHẦN A: HEADER & BỘ LỌC THỜI GIAN */}
      {/* ========================================================================= */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-5 md:p-6 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#224783]/10 text-[#224783] flex items-center justify-center font-bold">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-black text-slate-900 tracking-tight">
                  Tổng quan hệ thống
                </h1>
                <p className="text-xs text-slate-500 mt-0.5">
                  Thống kê người dùng, dòng tiền Xu & hiệu năng ứng dụng AI
                </p>
              </div>
            </div>
          </div>

          {/* Time Filter Dropdown & Refresh Button */}
          <div className="flex items-center space-x-2 self-start sm:self-center">
            {/* Popover Date Range Picker (Dropdown menu with presets + 2-month calendar) */}
            <DateRangePicker
              value={dateRange}
              onChange={(newRange) => setDateRange(newRange)}
              isLoading={isLoading || isFetching}
            />

            {/* Refresh Button */}
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Làm mới dữ liệu"
              className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50 cursor-pointer shadow-xs"
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-[#224783]' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* XỬ LÝ TRẠNG THÁI: IS ERROR */}
      {/* ========================================================================= */}
      {isError && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 mx-auto flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="max-w-md mx-auto">
            <h3 className="text-sm font-bold text-rose-900">Không thể tải dữ liệu Tổng quan</h3>
            <p className="text-xs text-rose-600 mt-1">
              {(error as any)?.response?.data?.message || (error as any)?.message || 'Đã xảy ra lỗi khi kết nối tới máy chủ. Vui lòng kiểm tra lại kết nối mạng hoặc phiên đăng nhập Admin.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer shadow-xs"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Thử lại ngay</span>
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* PHẦN B: THẺ TỔNG QUAN (SUMMARY CARDS - LƯỚI 3x2 TỐI GIẢN) */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Thẻ 1: Người dùng mới */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Người dùng mới</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <UserPlus className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            {isLoading ? (
              <div className="h-8 w-24 bg-slate-200 animate-pulse rounded-md" />
            ) : (
              <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                {formatNumber(summary?.newUsers)}
              </div>
            )}
            <div className="text-xs text-slate-400 mt-1 font-medium">
              {getTimeframeLabel(true)}
            </div>
          </div>
        </div>

        {/* Thẻ 2: Người dùng hoạt động (activeUsers) */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Người dùng hoạt động</span>
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Activity className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            {isLoading ? (
              <div className="h-8 w-24 bg-slate-200 animate-pulse rounded-md" />
            ) : (
              <div className="text-2xl sm:text-3xl font-black text-indigo-600 tracking-tight">
                {formatNumber(summary?.activeUsers)}
              </div>
            )}
            <div className="text-xs text-slate-400 mt-1 font-medium">
              {getTimeframeLabel(true)}
            </div>
          </div>
        </div>

        {/* Thẻ 3: VIP */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">VIP</span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Crown className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            {isLoading ? (
              <div className="h-8 w-20 bg-slate-200 animate-pulse rounded-md" />
            ) : (
              <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                {formatNumber(summary?.currentVips ?? summary?.vipUpgrades)}
              </div>
            )}
            <div className="flex items-center justify-between mt-1 text-xs">
              <span className="inline-flex items-center space-x-0.5 font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded text-[11px]">
                <ArrowUpRight className="w-3 h-3 stroke-[2.5]" />
                <span>+{formatNumber(summary?.vipUpgrades)} mới</span>
              </span>
              <span className="text-slate-400 font-medium text-xs">
                {getTimeframeLabel(true)}
              </span>
            </div>
          </div>
        </div>

        {/* Thẻ 4: SVIP */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">SVIP</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Gem className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            {isLoading ? (
              <div className="h-8 w-20 bg-slate-200 animate-pulse rounded-md" />
            ) : (
              <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                {formatNumber(summary?.currentSvips ?? summary?.svipUpgrades)}
              </div>
            )}
            <div className="flex items-center justify-between mt-1 text-xs">
              <span className="inline-flex items-center space-x-0.5 font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded text-[11px]">
                <ArrowUpRight className="w-3 h-3 stroke-[2.5]" />
                <span>+{formatNumber(summary?.svipUpgrades)} mới</span>
              </span>
              <span className="text-slate-400 font-medium text-xs">
                {getTimeframeLabel(true)}
              </span>
            </div>
          </div>
        </div>

        {/* Thẻ 5: Xu phát hành (totalMinted) */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Xu phát hành</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Coins className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            {isLoading ? (
              <div className="h-8 w-28 bg-slate-200 animate-pulse rounded-md" />
            ) : (
              <div className="text-2xl sm:text-3xl font-black text-emerald-600 tracking-tight">
                +{formatNumber(summary?.totalMinted)}
              </div>
            )}
            <div className="text-xs text-slate-400 mt-1 font-medium">
              {getTimeframeLabel(true)}
            </div>
          </div>
        </div>

        {/* Thẻ 6: Xu tiêu hao (totalBurned) */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Xu tiêu hao</span>
            <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
              <Flame className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            {isLoading ? (
              <div className="h-8 w-28 bg-slate-200 animate-pulse rounded-md" />
            ) : (
              <div className="text-2xl sm:text-3xl font-black text-rose-600 tracking-tight">
                -{formatNumber(summary?.totalBurned)}
              </div>
            )}
            <div className="text-xs text-slate-400 mt-1 font-medium">
              {getTimeframeLabel(true)}
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* PHẦN C: BIỂU ĐỒ PHÂN TÍCH (CHARTS) */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Biểu đồ 1: Tăng trưởng Người dùng (2 Line: newUsers & activeUsers) */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="pb-3 border-b border-slate-100 mb-4">
              <h2 className="text-sm font-bold text-slate-900 tracking-tight">
                Tăng trưởng Người dùng
              </h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Lượt đăng ký mới và tài khoản hoạt động
              </p>
            </div>

            <div className="h-[280px] w-full">
              {isLoading ? (
                <div className="w-full h-full flex flex-col items-center justify-center space-y-2 bg-slate-50/50 rounded-xl">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-slate-400">Đang tải dữ liệu biểu đồ...</span>
                </div>
              ) : chartData.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-xs text-slate-400 bg-slate-50 rounded-xl">
                  Chưa có dữ liệu biểu đồ cho khoảng thời gian này
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11, fill: '#64748b' }} 
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#64748b' }} 
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        borderRadius: '12px', 
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
                        border: '1px solid #e2e8f0',
                        fontSize: '12px'
                      }}
                      formatter={(val: any, name: any) => [
                        formatNumber(Number(val)), 
                        name === 'newUsers' ? 'Đăng ký mới' : 'Đang hoạt động'
                      ]}
                      labelFormatter={(label) => `Ngày: ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                      formatter={(value) => (value === 'newUsers' ? 'Đăng ký mới' : 'Đang hoạt động')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="newUsers" 
                      name="newUsers"
                      stroke="#2563eb" 
                      strokeWidth={2.5} 
                      dot={{ r: 3, fill: '#2563eb' }}
                      activeDot={{ r: 6 }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="activeUsers" 
                      name="activeUsers"
                      stroke="#10b981" 
                      strokeWidth={2.5} 
                      dot={{ r: 3, fill: '#10b981' }}
                      activeDot={{ r: 6 }} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Biểu đồ 2: Dòng tiền Xu */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="pb-3 border-b border-slate-100 mb-4">
              <h2 className="text-sm font-bold text-slate-900 tracking-tight">
                Dòng tiền Xu
              </h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Tương quan giữa phát hành và tiêu hao
              </p>
            </div>

            <div className="h-[280px] w-full">
              {isLoading ? (
                <div className="w-full h-full flex flex-col items-center justify-center space-y-2 bg-slate-50/50 rounded-xl">
                  <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-slate-400">Đang tải dữ liệu biểu đồ...</span>
                </div>
              ) : chartData.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-xs text-slate-400 bg-slate-50 rounded-xl">
                  Chưa có dữ liệu biểu đồ cho khoảng thời gian này
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11, fill: '#64748b' }} 
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#64748b' }} 
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                      tickFormatter={(val) => (val >= 1000 ? `${val / 1000}k` : val)}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        borderRadius: '12px', 
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
                        border: '1px solid #e2e8f0',
                        fontSize: '12px'
                      }}
                      formatter={(val: any, name: any) => [
                        `${formatNumber(Number(val))} Xu`, 
                        name === 'totalMinted' ? 'Xu phát hành' : 'Xu tiêu hao'
                      ]}
                      labelFormatter={(label) => `Ngày: ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                      formatter={(value) => (value === 'totalMinted' ? 'Xu phát hành (Cấp ra)' : 'Xu tiêu hao (Thu hồi)')}
                    />
                    <Bar 
                      dataKey="totalMinted" 
                      name="totalMinted"
                      fill="#10b981" 
                      radius={[4, 4, 0, 0]} 
                    />
                    <Bar 
                      dataKey="totalBurned" 
                      name="totalBurned"
                      fill="#f43f5e" 
                      radius={[4, 4, 0, 0]} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* PHẦN D: BẢNG XẾP HẠNG ỨNG DỤNG AI (LEADERBOARD) */}
      {/* ========================================================================= */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs overflow-hidden">
        <div className="p-5 md:p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <div className="flex items-center space-x-2">
              <Bot className="w-5 h-5 text-[#224783]" />
              <h2 className="text-base font-bold text-slate-900">
                Bảng Xếp hạng Ứng dụng AI
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Thống kê hiệu năng gọi API Gemini theo từng ứng dụng
            </p>
          </div>

          <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full self-start sm:self-center">
            {topApps.length} Ứng dụng tích hợp
          </span>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200/80 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                <th className="py-3 px-5">Mã & Tên Ứng Dụng</th>
                <th className="py-3 px-5 text-right">Tổng Lượt Gọi</th>
                <th className="py-3 px-5 text-right">Thành Công</th>
                <th className="py-3 px-5 text-right">Số Lượt Lỗi</th>
                <th className="py-3 px-5 text-center">Tỷ Lệ Thành Công</th>
                <th className="py-3 px-5 text-center">Trạng Thái Gemini</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs">
              {isLoading ? (
                // Skeletons
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="py-4 px-5">
                      <div className="h-4 w-36 bg-slate-200 rounded" />
                    </td>
                    <td className="py-4 px-5 text-right">
                      <div className="h-4 w-16 bg-slate-200 rounded ml-auto" />
                    </td>
                    <td className="py-4 px-5 text-right">
                      <div className="h-4 w-16 bg-slate-200 rounded ml-auto" />
                    </td>
                    <td className="py-4 px-5 text-right">
                      <div className="h-4 w-12 bg-slate-200 rounded ml-auto" />
                    </td>
                    <td className="py-4 px-5">
                      <div className="h-4 w-20 bg-slate-200 rounded mx-auto" />
                    </td>
                    <td className="py-4 px-5">
                      <div className="h-4 w-24 bg-slate-200 rounded mx-auto" />
                    </td>
                  </tr>
                ))
              ) : topApps.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-400 text-xs">
                    Chưa có ứng dụng nào được ghi nhận lượt gọi AI trong thời gian này
                  </td>
                </tr>
              ) : (
                topApps.map((app: DashboardTopApp, index: number) => {
                  const meta = APP_NAMES[app.appId] || {
                    name: app.appName || `Ứng dụng ${app.appId}`,
                    icon: '⚡',
                    tag: 'AI App'
                  };
                  const total = app.totalCalls || 0;
                  const success = app.successCount || 0;
                  const errors = app.errorCount || 0;
                  const rate = total > 0 ? ((success / total) * 100).toFixed(1) : '100.0';
                  const hasErrors = errors > 0;

                  return (
                    <tr 
                      key={app.appId || index} 
                      className="hover:bg-slate-50/70 transition-colors"
                    >
                      {/* Mã & Tên Ứng Dụng */}
                      <td className="py-3.5 px-5">
                        <div className="flex items-center space-x-3">
                          <span className="text-xl shrink-0">{meta.icon}</span>
                          <div>
                            <div className="font-bold text-slate-900 flex items-center space-x-1.5">
                              <span>{meta.name}</span>
                              <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                {app.appId}
                              </span>
                            </div>
                            <span className="text-[10px] text-slate-400">
                              {meta.tag}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Tổng lượt gọi */}
                      <td className="py-3.5 px-5 text-right font-extrabold text-slate-800 font-mono">
                        {formatNumber(total)}
                      </td>

                      {/* Thành công */}
                      <td className="py-3.5 px-5 text-right font-bold text-emerald-600 font-mono">
                        {formatNumber(success)}
                      </td>

                      {/* Số lượt Lỗi - Highlight đỏ cảnh báo nếu errorCount > 0 */}
                      <td className="py-3.5 px-5 text-right font-mono">
                        {hasErrors ? (
                          <span className="inline-flex items-center space-x-1 text-rose-600 font-extrabold bg-rose-50 px-2 py-0.5 rounded-lg border border-rose-200">
                            <AlertTriangle className="w-3 h-3 text-rose-600 shrink-0" />
                            <span>{formatNumber(errors)} lỗi</span>
                          </span>
                        ) : (
                          <span className="text-slate-400 font-medium">0</span>
                        )}
                      </td>

                      {/* Tỷ lệ thành công */}
                      <td className="py-3.5 px-5 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className={`font-bold font-mono ${Number(rate) >= 98 ? 'text-emerald-600' : Number(rate) >= 90 ? 'text-amber-600' : 'text-rose-600'}`}>
                            {rate}%
                          </span>
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                            <div 
                              className={`h-full rounded-full ${Number(rate) >= 98 ? 'bg-emerald-500' : Number(rate) >= 90 ? 'bg-amber-500' : 'bg-rose-500'}`}
                              style={{ width: `${Math.min(100, Math.max(0, Number(rate)))}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Trạng thái Gemini */}
                      <td className="py-3.5 px-5 text-center">
                        {hasErrors ? (
                          <span className="inline-flex items-center space-x-1 text-[11px] font-bold text-rose-700 bg-rose-100/80 px-2.5 py-1 rounded-full border border-rose-200">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>Cần kiểm tra API</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 text-[11px] font-bold text-emerald-700 bg-emerald-100/80 px-2.5 py-1 rounded-full border border-emerald-200">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Hoạt động tốt</span>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
